import { HelpCircle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * FeatureHint — small (i) icon that opens a click-triggered popover with
 * micro-content explaining what a feature is and how it helps the user.
 *
 * Different from `InfoTooltip` (which is a full-modal jargon dictionary):
 *   - Popover, not modal → lighter feel, doesn't block the page.
 *   - Click-triggered on all viewports (touch-friendly, no hover-only trap).
 *   - Radix Popover auto-collides with viewport edges → safe on mobile.
 *   - Compact 260-296px width fits inside a 375-viewport phone with margin.
 *
 * Usage:
 *   <FeatureHint
 *     title="Profile Completion"
 *     body="Filling every field unlocks M-Pesa payouts and reduces KYC review time."
 *     testId="hint-profile-completion"
 *   />
 */
export default function FeatureHint({
    title,
    body,
    bullets,          // optional array of short strings
    footer,           // optional CTA-ish line
    size = "sm",      // "sm" (14px icon) | "md" (16px icon)
    align = "start",  // Radix align: 'start' | 'center' | 'end'
    side = "bottom",  // Radix side
    testId,
    className = "",
}) {
    const iconClass = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
    const kebab = (title || "hint").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={`Explain: ${title}`}
                    data-testid={testId || `feature-hint-${kebab}`}
                    className={`inline-flex items-center justify-center align-middle text-muted-foreground hover:text-primary transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}
                >
                    <HelpCircle className={iconClass} />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align={align}
                side={side}
                sideOffset={6}
                collisionPadding={12}
                className="w-[min(20rem,calc(100vw-2rem))] p-4"
                data-testid={`${testId || `feature-hint-${kebab}`}-popover`}
            >
                <div className="flex items-start gap-2 mb-1.5">
                    <div className="w-1 h-4 rounded-sm bg-primary shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="font-extrabold text-sm tracking-tight leading-snug">{title}</div>
                </div>
                {body && (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        {body}
                    </p>
                )}
                {bullets?.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs">
                        {bullets.map((b) => (
                            <li key={b} className="flex gap-2 leading-snug">
                                <span className="text-primary font-bold shrink-0">·</span>
                                <span className="text-foreground/80">{b}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {footer && (
                    <div className="mt-3 pt-2 border-t border-border text-[11px] text-primary font-bold">
                        {footer}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
